import cropImg from '@lemonneko/crop-empty-pixels'
import localforage from 'localforage'

import { Application } from '@pixi/app'
import { extensions } from '@pixi/extensions'
import { Ticker, TickerPlugin } from '@pixi/ticker'
import { until } from '@vueuse/core'
import { nanoid } from 'nanoid'
import { defineStore } from 'pinia'
import { ref } from 'vue'

export enum DisplayModelFormat {
  Live2dZip = 'live2d-zip', // Cubism 4.x ZIP
  Live2dV2Zip = 'live2d-v2-zip', // Cubism 2.x ZIP
  Live2dDirectory = 'live2d-directory',
  VRM = 'vrm',
  PMXZip = 'pmx-zip',
  PMXDirectory = 'pmx-directory',
  PMD = 'pmd',
  SpriteSet = 'sprite-set', // 图片立绘集合（多张表情图片）
}

export type DisplayModel
  = | DisplayModelFile
    | DisplayModelURL

export interface DisplayModelFile {
  id: string
  format: DisplayModelFormat
  type: 'file'
  file: File
  name: string
  previewImage?: string
  importedAt: number
}

export interface DisplayModelURL {
  id: string
  format: DisplayModelFormat
  type: 'url'
  url: string
  name: string
  previewImage?: string
  importedAt: number
}

const displayModelsPresets: DisplayModel[] = [
  { id: 'preset-live2d-pro', format: DisplayModelFormat.Live2dDirectory, type: 'url', url: '/assets/live2d/models/hiyori_pro_custom/hiyori_pro_zh/runtime/hiyori_pro_t11.model3.json', name: 'Hiyori Pro (目录模式)', previewImage: '/assets/live2d/models/hiyori/preview.png', importedAt: 1733113886840 },
  { id: 'preset-vrm-1', format: DisplayModelFormat.VRM, type: 'url', url: '/assets/vrm/models/AvatarSample-A/AvatarSample_A.vrm', name: 'AvatarSample_A', previewImage: '/assets/vrm/models/AvatarSample-A/preview.png', importedAt: 1733113886840 },
]

/**
 * 将 File 转换为 base64 data URL
 */
function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

/**
 * 缩小图片 data URL 到指定尺寸
 */
function resizeImageDataUrl(dataUrl: string, maxWidth: number, maxHeight: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => {
      const canvas = document.createElement('canvas')
      let { width, height } = img
      if (width > maxWidth || height > maxHeight) {
        const ratio = Math.min(maxWidth / width, maxHeight / height)
        width = Math.round(width * ratio)
        height = Math.round(height * ratio)
      }
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d')!
      ctx.drawImage(img, 0, 0, width, height)
      resolve(canvas.toDataURL('image/png'))
    }
    img.onerror = reject
    img.src = dataUrl
  })
}

export const useDisplayModelsStore = defineStore('display-models', () => {
  const displayModels = ref<DisplayModel[]>([])

  const displayModelsFromIndexedDBLoading = ref(false)
  const displayModelsLoaded = ref(false) // 标记是否已加载

  async function loadDisplayModelsFromIndexedDB() {
    // 如果已经加载过，直接返回
    if (displayModelsLoaded.value) {
      return
    }

    // 如果正在加载，等待加载完成
    await until(displayModelsFromIndexedDBLoading).toBe(false)

    displayModelsFromIndexedDBLoading.value = true
    const models = [...displayModelsPresets]

    try {
      await localforage.iterate<{ format: DisplayModelFormat, file: File, importedAt: number, previewImage?: string }, void>((val, key) => {
        if (key.startsWith('display-model-')) {
          models.push({ id: key, format: val.format, type: 'file', file: val.file, name: val.file.name, importedAt: val.importedAt, previewImage: val.previewImage })
        }
      })
    }
    catch (err) {
      console.error(err)
    }

    displayModels.value = models.sort((a, b) => b.importedAt - a.importedAt)
    displayModelsFromIndexedDBLoading.value = false
    displayModelsLoaded.value = true // 标记为已加载
  }

  async function getDisplayModel(id: string) {
    await until(displayModelsFromIndexedDBLoading).toBe(false)
    const modelFromFile = await localforage.getItem<DisplayModelFile>(id)
    if (modelFromFile) {
      return modelFromFile
    }

    // Fallback to in-memory presets if not found in localforage
    return displayModelsPresets.find(model => model.id === id)
  }

  /**
   * 检测 Live2D ZIP 文件的 Cubism 版本
   * @param file ZIP 文件
   * @returns 'cubism2' | 'cubism4' | 'unknown'
   */
  async function detectCubismVersion(file: File): Promise<'cubism2' | 'cubism4' | 'unknown'> {
    try {
      const JSZip = (await import('jszip')).default
      const zip = await JSZip.loadAsync(file)
      const files = Object.keys(zip.files)

      // 检测 Cubism 4.x: .moc3 或 model3.json
      const hasMoc3 = files.some(f => f.endsWith('.moc3'))
      const hasModel3Json = files.some(f => f.endsWith('model3.json'))
      if (hasMoc3 || hasModel3Json) {
        return 'cubism4'
      }

      // 检测 Cubism 2.x: .moc 或 model.json (非 model3.json)
      const hasMoc = files.some(f => f.endsWith('.moc'))
      const hasModelJson = files.some(f => f.endsWith('model.json') || f.endsWith('.model.json'))
      if (hasMoc || hasModelJson) {
        return 'cubism2'
      }

      return 'unknown'
    }
    catch (error) {
      console.error('[Model Detection] 检测失败:', error)
      return 'unknown'
    }
  }

  async function loadLive2DModelPreview(file: File, version: 'cubism2' | 'cubism4' = 'cubism4') {
    // 动态加载 Live2D SDK 和相关模块
    const { ensureLive2DSDKLoaded } = await import('../utils/live2d-sdk-loader')
    await ensureLive2DSDKLoaded()

    // 加载 zip-loader 副作用（配置 ZipLoader）
    await import('../utils/live2d-zip-loader')
    await import('../utils/live2d-v2-zip-loader')

    const { Live2DModel } = await import('pixi-live2d-display/cubism4')

    Live2DModel.registerTicker(Ticker)
    extensions.add(TickerPlugin)

    const offscreenCanvas = document.createElement('canvas')
    offscreenCanvas.width = 720
    offscreenCanvas.height = 1280
    offscreenCanvas.style.position = 'absolute'
    offscreenCanvas.style.top = '0'
    offscreenCanvas.style.left = '0'
    offscreenCanvas.style.objectFit = 'cover'
    offscreenCanvas.style.display = 'block'
    offscreenCanvas.style.zIndex = '10000000000'
    offscreenCanvas.style.opacity = '0'
    document.body.appendChild(offscreenCanvas)

    const app = new Application({
      view: offscreenCanvas,
      preserveDrawingBuffer: true,
      backgroundAlpha: 0,
    })

    const modelInstance = new Live2DModel()
    const objUrl = URL.createObjectURL(file)
    const res = await fetch(objUrl)
    const blob = await res.blob()

    try {
      if (version === 'cubism2') {
        const { Live2DFactory: Live2DFactoryV2 } = await import('pixi-live2d-display/cubism2')
        await Live2DFactoryV2.setupLive2DModel(modelInstance, [new File([blob], file.name)], { autoInteract: false })
      }
      else {
        const { Live2DFactory } = await import('pixi-live2d-display/cubism4')
        await Live2DFactory.setupLive2DModel(modelInstance, [new File([blob], file.name)], { autoInteract: false })
      }
    }
    catch (error) {
      app.destroy()
      document.body.removeChild(offscreenCanvas)
      URL.revokeObjectURL(objUrl)
      console.error('[Display Models] Failed to load Live2D model:', file.name, error)
      return
    }

    app.stage.addChild(modelInstance)

    // transforms
    modelInstance.x = 275
    modelInstance.y = 450
    modelInstance.width = offscreenCanvas.width
    modelInstance.height = offscreenCanvas.height
    modelInstance.scale.set(0.1, 0.1)
    modelInstance.anchor.set(0.5, 0.5)

    await new Promise<void>((resolve) => {
      if ((modelInstance as any).internalModel) {
        resolve()
        return
      }
      const timer = setTimeout(() => resolve(), 3000)
      modelInstance.once('load', () => {
        clearTimeout(timer)
        resolve()
      })
    })
    app.renderer.render(app.stage)

    const croppedCanvas = cropImg(offscreenCanvas)

    // padding to 12:16
    const paddingCanvas = document.createElement('canvas')
    paddingCanvas.width = croppedCanvas.width > croppedCanvas.height / 16 * 12 ? croppedCanvas.width : croppedCanvas.height / 16 * 12
    paddingCanvas.height = paddingCanvas.width / 12 * 16
    const paddingCanvasCtx = paddingCanvas.getContext('2d')!

    paddingCanvasCtx.drawImage(croppedCanvas, (paddingCanvas.width - croppedCanvas.width) / 2, (paddingCanvas.height - croppedCanvas.height) / 2, croppedCanvas.width, croppedCanvas.height)
    const paddingDataUrl = paddingCanvas.toDataURL()

    app.destroy()
    document.body.removeChild(offscreenCanvas)
    URL.revokeObjectURL(objUrl)

    // return dataUrl
    return paddingDataUrl
  }

  async function addDisplayModelFromFolder(files: File[]) {
    await until(displayModelsFromIndexedDBLoading).toBe(false)

    // 将文件夹中的文件打包成 ZIP
    const JSZip = (await import('jszip')).default
    const zip = new JSZip()

    // 提取文件夹名称（从第一个文件的路径）
    let folderName = 'live2d-model'
    if (files.length > 0 && files[0].webkitRelativePath) {
      const parts = files[0].webkitRelativePath.split('/')
      folderName = parts[0] || 'live2d-model'
    }

    // 添加所有文件到 ZIP（保持相对路径）
    for (const file of files) {
      // 使用 webkitRelativePath 保持文件夹结构，或者使用文件名
      const path = file.webkitRelativePath || file.name
      // 移除第一层文件夹名（因为我们要把内容打包，不包括文件夹本身）
      const relativePath = path.split('/').slice(1).join('/')
      if (relativePath) {
        zip.file(relativePath, file)
      }
    }

    // 生成 ZIP blob
    const zipBlob = await zip.generateAsync({ type: 'blob' })
    const zipFile = new File([zipBlob], `${folderName}.zip`, { type: 'application/zip' })

    // 使用现有的 ZIP 处理逻辑
    await addDisplayModel(DisplayModelFormat.Live2dZip, zipFile)
  }

  async function addDisplayModel(format: DisplayModelFormat, file: File) {
    await until(displayModelsFromIndexedDBLoading).toBe(false)

    // 如果是 Live2D ZIP，先检测版本
    let detectedFormat = format
    if (format === DisplayModelFormat.Live2dZip) {
      const version = await detectCubismVersion(file)
      console.info('[Display Models] 检测到模型版本:', version, '文件:', file.name)

      if (version === 'cubism2') {
        detectedFormat = DisplayModelFormat.Live2dV2Zip
      }
      else if (version === 'cubism4') {
        detectedFormat = DisplayModelFormat.Live2dZip
      }
      else {
        console.warn('[Display Models] 无法识别模型版本，默认使用 Cubism 4.x')
        detectedFormat = DisplayModelFormat.Live2dZip
      }
    }

    const newDisplayModel: DisplayModelFile = { id: `display-model-${nanoid()}`, format: detectedFormat, type: 'file', file, name: file.name, importedAt: Date.now() }

    if (detectedFormat === DisplayModelFormat.Live2dZip || detectedFormat === DisplayModelFormat.Live2dV2Zip) {
      const version = detectedFormat === DisplayModelFormat.Live2dV2Zip ? 'cubism2' : 'cubism4'
      const previewImage = await loadLive2DModelPreview(file, version)
      if (!previewImage) {
        console.error('[Display Models] Failed to load Live2D model preview:', file.name)
        // eslint-disable-next-line no-alert
        alert(`❌ Live2D 模型加载失败！\n\n检测到的模型版本: ${version === 'cubism2' ? 'Cubism 2.x' : 'Cubism 4.x'}\n\n可能的原因：\n• ZIP 文件结构不正确\n• 模型文件损坏或不完整\n• 不支持的模型格式\n\n请检查浏览器控制台（F12）查看详细错误信息`)
        return
      }

      newDisplayModel.previewImage = previewImage
    }

    displayModels.value.unshift(newDisplayModel)

    localforage.setItem<DisplayModelFile>(newDisplayModel.id, newDisplayModel)
      .catch(err => console.error(err))
  }

  async function renameDisplayModel(id: string, name: string) {
    await until(displayModelsFromIndexedDBLoading).toBe(false)
    const displayModel = await localforage.getItem<DisplayModelFile>(id)
    if (!displayModel)
      return

    // 更新对象并写回 localforage
    displayModel.name = name
    await localforage.setItem(id, displayModel)

    // 同步更新内存中的列表
    const index = displayModels.value.findIndex(m => m.id === id)
    if (index !== -1) {
      displayModels.value[index] = { ...displayModels.value[index], name }
    }
  }

  /**
   * 从多张图片文件创建 Sprite 立绘集合
   * @param files 图片文件数组
   * @param name 立绘集合名称
   * @returns 新创建的 DisplayModel ID
   */
  async function addDisplayModelFromSpriteImages(files: File[], name?: string) {
    await until(displayModelsFromIndexedDBLoading).toBe(false)

    if (files.length === 0) {
      console.warn('[Display Models] No files provided for sprite set')
      return
    }

    const modelId = `display-model-${nanoid()}`
    const modelName = name || `立绘集合 (${files.length}张)`

    // 生成预览图：使用第一张图片作为预览
    let previewImage: string | undefined
    try {
      const rawPreview = await fileToDataUrl(files[0])
      // 如果预览图太大，缩小它
      previewImage = rawPreview.length > 200000
        ? await resizeImageDataUrl(rawPreview, 192, 256)
        : rawPreview
    }
    catch (err) {
      console.error('[Display Models] Failed to generate sprite preview:', err)
    }

    // 将图片存储到 sprite 专用的 localforage 实例（storeName 必须与 sprite.ts 一致）
    const spriteStorage = localforage.createInstance({ name: 'meowchat-sprites', storeName: 'sprite-sets' })
    const spriteImages: Array<{ id: string, name: string, dataUrl: string, emotion?: string, isDefault: boolean }> = []

    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      try {
        const dataUrl = await fileToDataUrl(file)
        spriteImages.push({
          id: nanoid(),
          name: file.name,
          dataUrl,
          isDefault: i === 0, // 第一张图片为默认立绘
        })
      }
      catch (err) {
        console.error(`[Display Models] Failed to process sprite image: ${file.name}`, err)
      }
    }

    if (spriteImages.length === 0) {
      console.error('[Display Models] No valid sprite images processed')
      return
    }

    // 保存立绘数据到 sprite storage
    await spriteStorage.setItem(modelId, {
      modelId,
      name: modelName,
      images: spriteImages,
      createdAt: Date.now(),
    })

    // 创建 DisplayModel 记录（使用一个占位 File 对象）
    const placeholderBlob = new Blob([JSON.stringify({ type: 'sprite-set', imageCount: spriteImages.length })], { type: 'application/json' })
    const placeholderFile = new File([placeholderBlob], `${modelName}.sprite`, { type: 'application/json' })

    const newDisplayModel: DisplayModelFile = {
      id: modelId,
      format: DisplayModelFormat.SpriteSet,
      type: 'file',
      file: placeholderFile,
      name: modelName,
      importedAt: Date.now(),
      previewImage,
    }

    displayModels.value.unshift(newDisplayModel)

    localforage.setItem<DisplayModelFile>(modelId, newDisplayModel)
      .catch(err => console.error(err))

    console.info(`[Display Models] Sprite set created: ${modelName} (${spriteImages.length} images)`)
    return modelId
  }

  async function removeDisplayModel(id: string) {
    await until(displayModelsFromIndexedDBLoading).toBe(false)

    // 如果是 Sprite 类型，同时清理 sprite storage
    const model = displayModels.value.find(m => m.id === id)
    if (model?.format === DisplayModelFormat.SpriteSet) {
      const spriteStorage = localforage.createInstance({ name: 'meowchat-sprites', storeName: 'sprite-sets' })
      await spriteStorage.removeItem(id).catch(err => console.error(err))
    }

    await localforage.removeItem(id)
    displayModels.value = displayModels.value.filter(model => model.id !== id)
  }

  return {
    displayModels,
    displayModelsFromIndexedDBLoading,
    displayModelsLoaded,

    loadDisplayModelsFromIndexedDB,
    getDisplayModel,
    addDisplayModel,
    addDisplayModelFromFolder,
    addDisplayModelFromSpriteImages,
    renameDisplayModel,
    removeDisplayModel,
  }
})
